import { useEffect, useState } from "react";
import Button from "./Button";
import { ImageView } from "./Image";
import Spinner from "./Spinners";
import { Tag } from "./Tag";
import { CompetitorAdsCard } from "./CompetitorAdsCard";
import { RegenerateCampaignsModal } from "./RegenerateCampaignsModal";
import { useDebounce } from "../helpers/useDebounce";
import {
  useCompetitorStep,
  useSaveCompetitorStep,
} from "../helpers/hooks/use-competitor-step";
import {
  useCampaignRegeneration,
  useRegenerateCampaigns,
} from "../helpers/hooks/use-competitor-keywords";
import {
  competitorImageUrl,
  isPlacesAvailable,
  locateLodging,
  nearbyLodging,
  suggestLodging,
} from "../helpers/places";
import type { LatLng } from "../helpers/places";
import {
  competitorKey,
  diffCompetitorSets,
  isOwnHotel,
  isSameCompetitor,
} from "../helpers/competitor-set";
import type {
  Competitor,
  CompetitorStepAnswers,
  CompetitorStepSnapshot,
} from "../types/competitors";

const SEARCH_DEBOUNCE_MS = 500;

const CARD_CLASS = "rounded-md border border-border-primary px-4 py-3 text-sm";
const CARD_LABEL_CLASS =
  "text-xs font-medium text-content-secondary uppercase mb-2";
const TEXTAREA_CLASS =
  "w-full rounded-md border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-primary resize-y";

function distanceLabel(distance: number | undefined): string | null {
  return typeof distance === "number" && Number.isFinite(distance)
    ? `${distance} mi away`
    : null;
}

function CompetitorRow({
  competitor,
  tag,
  action,
  onClick,
}: {
  competitor: Competitor;
  tag?: React.ReactNode;
  action?: React.ReactNode;
  onClick?: () => void;
}) {
  const image = competitorImageUrl(competitor.imageUrl);
  const detail = [competitor.address, distanceLabel(competitor.distance)]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`flex items-center gap-3 rounded-md border border-border-primary bg-white px-3 py-2 ${
        onClick ? "cursor-pointer hover:bg-neutral-50 transition-colors" : ""
      }`}
    >
      <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-neutral-100">
        {image && (
          <ImageView className="w-full h-full object-cover" src={image} alt="" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-content-primary truncate">
            {competitor.name}
          </span>
          {tag}
        </div>
        <div className="text-xs text-content-secondary truncate">
          {detail || "—"}
        </div>
      </div>
      {action}
    </div>
  );
}

function RowButton({
  label,
  title,
  onClick,
}: {
  label: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={title}
      title={title}
      className="text-content-secondary hover:text-content-primary text-xl leading-none px-2"
    >
      {label}
    </button>
  );
}

function rowTag(competitor: Competitor, isNew: boolean) {
  if (isNew) return <Tag tone="blue">new — not saved yet</Tag>;
  if (competitor.aggregatePricingHotelCode) {
    return <Tag tone="green">rate-shopped</Tag>;
  }
  return null;
}

/**
 * Search box + results for adding a hotel. An empty box lists the lodging
 * around the hotel — the customer picker's own starting view — and typed
 * text goes through autocomplete so a partial name still finds the place.
 * The centre is the hotel's own coordinates when its record has them, else
 * wherever Places puts a lodging by that name; an unlocated hotel still
 * searches, just unbiased and without the nearby list.
 */
function AddCompetitor({
  snapshot,
  current,
  onAdd,
}: {
  snapshot: CompetitorStepSnapshot;
  current: Competitor[];
  onAdd: (competitor: Competitor) => void;
}) {
  const placesReady = isPlacesAvailable();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [near, setNear] = useState<LatLng | null>(snapshot.location);
  const [results, setResults] = useState<Competitor[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (snapshot.location || !snapshot.hotelName || !placesReady) return;
    let cancelled = false;
    locateLodging(snapshot.hotelName)
      .then((found) => {
        if (!cancelled) setNear(found);
      })
      .catch(() => {
        // Unlocated: the search still runs, just without a centre.
      });
    return () => {
      cancelled = true;
    };
  }, [snapshot.location, snapshot.hotelName, placesReady]);

  const text = debouncedQuery.trim();
  useEffect(() => {
    if (!placesReady || (!text && !near)) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setSearchError(null);
    const lookup = text ? suggestLodging(text, near) : nearbyLodging(near!);
    lookup
      .then((found) => {
        if (!cancelled) setResults(found);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSearchError(
            error instanceof Error ? error.message : "Search failed",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [text, near, placesReady]);

  const candidates = results.filter(
    (candidate) =>
      !isOwnHotel(candidate, snapshot) &&
      !current.some((chosen) => isSameCompetitor(chosen, candidate)),
  );
  const searched = text.length > 0;

  return (
    <div className={CARD_CLASS}>
      <div className={CARD_LABEL_CLASS}>Add a competitor</div>
      {!placesReady ? (
        <div className="text-content-secondary italic">
          Google Places did not load in this tab — reload the portal to search.
        </div>
      ) : (
        <>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                near
                  ? `Hotel name — searching near ${snapshot.hotelName ?? "the hotel"}`
                  : "Hotel name or address"
              }
              className="w-full rounded-md border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-primary"
            />
            {searching && (
              <Spinner className="!text-brand-primary absolute right-3 top-2.5" />
            )}
          </div>
          {searchError && (
            <div className="mt-2 text-xs text-red-700">{searchError}</div>
          )}
          {searched && !searching && !searchError && candidates.length === 0 && (
            <div className="mt-2 text-xs text-content-secondary italic">
              No lodging matched — try more of the name, or the name as Google
              Maps lists it.
            </div>
          )}
          {candidates.length > 0 && (
            <div className="mt-3 flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
              {!searched && (
                <div className="text-xs text-content-secondary">
                  Nearby lodging — type to search by name
                </div>
              )}
              {candidates.map((candidate) => (
                <CompetitorRow
                  key={competitorKey(candidate)}
                  competitor={candidate}
                  onClick={() => onAdd(candidate)}
                  action={
                    <RowButton
                      label="+"
                      title={`Add ${candidate.name}`}
                      onClick={() => onAdd(candidate)}
                    />
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AnswerField({
  label,
  question,
  value,
  onChange,
}: {
  label: string;
  question: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-content-primary">{label}</div>
      <div className="text-xs text-content-secondary mb-1.5">{question}</div>
      <textarea
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={TEXTAREA_CLASS}
      />
    </label>
  );
}

interface StepChanges {
  added: Competitor[];
  removed: Competitor[];
  uspChanged: boolean;
  amenitiesChanged: boolean;
}

function diffAnswers(
  saved: CompetitorStepAnswers,
  draft: CompetitorStepAnswers,
): StepChanges {
  return {
    ...diffCompetitorSets(saved.competitors, draft.competitors),
    uspChanged: saved.usp.trim() !== draft.usp.trim(),
    amenitiesChanged: saved.amenities.trim() !== draft.amenities.trim(),
  };
}

function hasStepChanges(changes: StepChanges): boolean {
  return (
    changes.added.length > 0 ||
    changes.removed.length > 0 ||
    changes.uspChanged ||
    changes.amenitiesChanged
  );
}

function changeSummary(changes: StepChanges): string {
  const names = (list: Competitor[]) => list.map((c) => c.name).join(", ");
  return [
    changes.added.length > 0 && `Adding ${names(changes.added)}`,
    changes.removed.length > 0 && `Removing ${names(changes.removed)}`,
    changes.uspChanged && "USP updated",
    changes.amenitiesChanged && "Amenities updated",
  ]
    .filter(Boolean)
    .join(" · ");
}

function answersOf(snapshot: CompetitorStepSnapshot): CompetitorStepAnswers {
  return {
    competitors: snapshot.competitors,
    usp: snapshot.usp,
    amenities: snapshot.amenities,
  };
}

/**
 * The onboarding Competitor step, editable after the fact — the customer's
 * own form locks once they continue past it, so the extra hotels and context
 * they email in later land here. Edits stage locally and go up as one save,
 * because each save fans out server-side (Slack notice, rate-shop code
 * lookups, and a compset rebuild for a hotel that has finished onboarding).
 */
export function CompetitorStepEditor({ userId }: { userId: string }) {
  const snapshot = useCompetitorStep(userId);
  const save = useSaveCompetitorStep(userId);
  const regeneration = useCampaignRegeneration(userId);
  const regenerate = useRegenerateCampaigns(userId);
  // null = mirror what is saved; a value = the operator's staged edits.
  const [draft, setDraft] = useState<CompetitorStepAnswers | null>(null);
  // Which sticky-bar button is in flight, so only it shows the spinner.
  const [pendingAction, setPendingAction] = useState<
    "save" | "save-regenerate" | null
  >(null);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);

  if (snapshot.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="!text-brand-primary" />
      </div>
    );
  }
  if (snapshot.error || !snapshot.data) {
    return (
      <div className="text-sm text-red-700">
        Could not load this hotel's onboarding answers.
      </div>
    );
  }

  const saved = answersOf(snapshot.data);
  const current = draft ?? saved;
  const changes = diffAnswers(saved, current);
  const dirty = hasStepChanges(changes);

  const edit = (patch: Partial<CompetitorStepAnswers>) =>
    setDraft((prev) => ({ ...(prev ?? saved), ...patch }));
  const add = (competitor: Competitor) => {
    if (current.competitors.some((c) => isSameCompetitor(c, competitor))) {
      return;
    }
    edit({ competitors: [...current.competitors, competitor] });
  };
  const remove = (competitor: Competitor) =>
    edit({
      competitors: current.competitors.filter(
        (c) => !isSameCompetitor(c, competitor),
      ),
    });
  const discard = () => setDraft(null);
  const commit = () => {
    setPendingAction("save");
    save.mutate(current, {
      onSuccess: () => setDraft(null),
      onSettled: () => setPendingAction(null),
    });
  };
  // Save first, regenerate only once the save is on the record — generation
  // reads the saved answers, not the draft.
  // The warning modal stays up, spinning, until the whole sequence settles.
  const commitAndRegenerate = () => {
    setPendingAction("save-regenerate");
    const finish = () => {
      setPendingAction(null);
      setConfirmingRegenerate(false);
    };
    save.mutate(current, {
      onSuccess: () => {
        setDraft(null);
        regenerate.mutate(undefined, { onSettled: finish });
      },
      onError: finish,
    });
  };
  const canRegenerate =
    !!regeneration.data && !regeneration.data.blockedReason;
  // Existing campaigns get superseded, so the warning comes before the click.
  const replacesExisting =
    canRegenerate && regeneration.data!.campaigns.length > 0;
  const startRegenerate = () =>
    replacesExisting ? setConfirmingRegenerate(true) : commitAndRegenerate();
  const busy = pendingAction !== null;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-content-secondary">
        What the customer answered on the Competitor step. Saving runs the same
        path as their own form — rate-shop codes are looked up for new hotels,
        the competitor change is posted to Slack, and a live pricing hotel's
        compset is rebuilt. Saving alone does not touch the campaigns: Save
        &amp; regenerate rebuilds them from the new answers while the hotel is
        not live yet, and the Google Ads card below adds competitors to a
        campaign that already is.
      </p>

      <div className={CARD_CLASS}>
        <div className={CARD_LABEL_CLASS}>
          Competitor set ({current.competitors.length})
        </div>
        {current.competitors.length === 0 ? (
          <div className="text-content-secondary italic">
            No competitors on record.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {current.competitors.map((competitor) => (
              <CompetitorRow
                key={competitorKey(competitor)}
                competitor={competitor}
                tag={rowTag(
                  competitor,
                  changes.added.some((c) => isSameCompetitor(c, competitor)),
                )}
                action={
                  <RowButton
                    label="×"
                    title={`Remove ${competitor.name}`}
                    onClick={() => remove(competitor)}
                  />
                }
              />
            ))}
          </div>
        )}
      </div>

      <AddCompetitor
        snapshot={snapshot.data}
        current={current.competitors}
        onAdd={add}
      />

      <CompetitorAdsCard userId={userId} dirty={dirty} />

      <div className={CARD_CLASS}>
        <div className={CARD_LABEL_CLASS}>USP &amp; amenities</div>
        <div className="flex flex-col gap-4">
          <AnswerField
            label="Unique selling proposition"
            question="What makes your hotel stand out from these competitors?"
            value={current.usp}
            onChange={(usp) => edit({ usp })}
          />
          <AnswerField
            label="Amenities"
            question="What amenities or offerings do you have that guests really value?"
            value={current.amenities}
            onChange={(amenities) => edit({ amenities })}
          />
        </div>
      </div>

      {dirty && (
        <div className="sticky bottom-0 -mx-6 -mb-5 px-6 py-3 bg-white border-t border-border-primary flex items-center gap-3">
          <div className="flex-1 min-w-0 text-xs text-content-secondary">
            {changeSummary(changes)}
            {replacesExisting &&
              ` · regenerating replaces ${regeneration.data!.campaigns.length} existing campaigns`}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={discard}
            disabled={busy}
          >
            Discard
          </Button>
          <Button
            variant={canRegenerate ? "outline" : "default"}
            size="sm"
            loading={pendingAction === "save"}
            disabled={busy && pendingAction !== "save"}
            onClick={commit}
          >
            Save changes
          </Button>
          {canRegenerate && (
            <Button
              size="sm"
              loading={pendingAction === "save-regenerate" && !confirmingRegenerate}
              disabled={busy && pendingAction !== "save-regenerate"}
              onClick={startRegenerate}
            >
              Save &amp; regenerate
            </Button>
          )}
        </div>
      )}

      {confirmingRegenerate && regeneration.data && (
        <RegenerateCampaignsModal
          state={regeneration.data}
          saveFirst
          pending={pendingAction === "save-regenerate"}
          onCancel={() => setConfirmingRegenerate(false)}
          onConfirm={commitAndRegenerate}
        />
      )}
    </div>
  );
}
